/**
 * §4.9 — status effect framework. Application, stacking, DoT ticking, expiry.
 * Step 11 of the sim order. Consumes no RNG (application chance rolls belong to §4.7 step 2,
 * which is the caller's responsibility and uses the `crit` stream).
 *
 * Data source: src/content/status-effects.json via src/core/content.ts.
 */

import { STEP } from '../core/loop';
import { statusById } from '../core/content';
import { MAX_STATUS_SLOTS, type EntityBase, type Enemy, type StatusInstance } from './entities';
import type { World } from './world';
import { dealStatusDamage } from './damage';

export const STATUS_BURN = 'status.burn';
export const STATUS_POISON = 'status.poison';
export const STATUS_SHOCK = 'status.shock';
export const STATUS_SLOW = 'status.slow';
export const STATUS_STUN = 'status.stun';
export const STATUS_WEAKEN = 'status.weaken';
export const STATUS_CORRODE = 'status.corrode';
export const STATUS_BLEED = 'status.bleed';

/** §4.9 — total movement slow is clamped at 40%. */
export const SLOW_CAP = 0.4;

type StackingModel = 'refresh' | 'stack' | 'intensity';

interface ResolvedStatus {
  stacking: StackingModel;
  maxStacks: number;
  tickRateHz: number;
  magnitude: number;
  durationS: number;
  bossResistance: number;
}

/** Resolved defs are cached at module scope; the content tables are frozen at boot (§16). */
const resolved = new Map<string, ResolvedStatus>();

function resolve(statusId: string): ResolvedStatus | null {
  const cached = resolved.get(statusId);
  if (cached !== undefined) return cached;
  const def = statusById.get(statusId);
  if (def === undefined) return null;
  const raw = def as unknown as Record<string, unknown>;
  const model = raw.stacking_model as StackingModel;
  const out: ResolvedStatus = {
    stacking: model === 'stack' || model === 'intensity' ? model : 'refresh',
    maxStacks: Math.max(1, Number(raw.max_stacks) || 1),
    tickRateHz: Number(raw.tick_rate_hz) || 0,
    magnitude: Number(raw.magnitude_per_stack) || 0,
    durationS: Number(raw.duration_s) || 0,
    bossResistance: Number(raw.boss_resistance) || 0,
  };
  resolved.set(statusId, out);
  return out;
}

const exhaustedPayload = { pool: 'status', denied_kind: '' };

/** §4.9 — bosses take `1 − boss_resistance`, elites `1 − boss_resistance / 2`. */
function resistanceFactor(target: EntityBase, def: ResolvedStatus): number {
  if (target.kind !== 'enemy') return 1;
  const e = target as Enemy;
  if (e.is_boss) return 1 - def.bossResistance;
  if (e.elite) return 1 - def.bossResistance / 2;
  return 1;
}

function findSlot(target: EntityBase, statusId: string): StatusInstance | null {
  const list = target.status_effects;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    if (s.stacks > 0 && s.status_id === statusId) return s;
  }
  return null;
}

function countInstances(target: EntityBase, statusId: string): number {
  const list = target.status_effects;
  let n = 0;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    if (s.stacks > 0 && s.status_id === statusId) n++;
  }
  return n;
}

function freeSlot(target: EntityBase): StatusInstance | null {
  const list = target.status_effects;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    if (list[i].stacks <= 0) return list[i];
  }
  return null;
}

function periodTicks(hz: number): number {
  if (hz <= 0) return 0;
  const p = Math.round(60 / hz);
  return p < 1 ? 1 : p;
}

function recount(target: EntityBase): void {
  const list = target.status_effects;
  let n = 0;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    if (list[i].stacks > 0) n++;
  }
  target.status_count = n;
}

function writeInstance(
  w: World,
  inst: StatusInstance,
  statusId: string,
  stacks: number,
  sourceId: number,
  magnitude: number,
  durationS: number,
  hz: number
): void {
  inst.status_id = statusId;
  inst.stacks = stacks;
  inst.source_id = sourceId;
  inst.tick_rate_hz = hz;
  inst.snapshot.magnitude_per_stack = magnitude;
  // +STEP so the first stepStatus pass on the application tick does not consume a frame of duration.
  inst.remaining_s = durationS + STEP;
  inst.next_tick = hz > 0 ? w.tick + periodTicks(hz) : Number.MAX_SAFE_INTEGER;
}

/**
 * §4.9 — apply `stacks` applications of `statusId` to `target`.
 * `magnitude <= 0` uses the content table's `magnitude_per_stack`.
 * Magnitude AND duration are scaled by elite/boss resistance at application, then snapshotted
 * (A.4 `StatusInstance.snapshot`). Applying past `max_stacks` or past the 8 preallocated slots
 * is denied per §14.3.
 */
export function applyStatus(
  w: World,
  target: EntityBase,
  statusId: string,
  stacks: number,
  sourceId: number,
  magnitude: number
): void {
  if (!target.alive || stacks <= 0) return;
  const def = resolve(statusId);
  if (def === null) return;
  const factor = resistanceFactor(target, def);
  if (factor <= 0) return; // boss_resistance 1.0 => immune
  const mag = (magnitude > 0 ? magnitude : def.magnitude) * factor;
  const dur = def.durationS * factor;
  if (dur <= 0) return;

  if (def.stacking === 'stack') {
    // Independent instances, each with its own timer and tick schedule.
    let live = countInstances(target, statusId);
    for (let i = 0; i < stacks; i++) {
      if (live >= def.maxStacks) break;
      const slot = freeSlot(target);
      if (slot === null) {
        exhaustedPayload.denied_kind = statusId;
        w.bus.emit('pool_exhausted', exhaustedPayload);
        break;
      }
      writeInstance(w, slot, statusId, 1, sourceId, mag, dur, def.tickRateHz);
      live++;
    }
    recount(target);
    return;
  }

  const existing = findSlot(target, statusId);
  if (existing !== null) {
    if (def.stacking === 'intensity') {
      const next = existing.stacks + stacks;
      existing.stacks = next > def.maxStacks ? def.maxStacks : next;
    }
    existing.snapshot.magnitude_per_stack = mag;
    existing.source_id = sourceId;
    existing.remaining_s = dur + STEP;
    return;
  }

  const slot = freeSlot(target);
  if (slot === null) {
    exhaustedPayload.denied_kind = statusId;
    w.bus.emit('pool_exhausted', exhaustedPayload);
    return;
  }
  const initial = def.stacking === 'intensity' ? Math.min(stacks, def.maxStacks) : 1;
  writeInstance(w, slot, statusId, initial, sourceId, mag, dur, def.tickRateHz);
  recount(target);
}

/** Clears every status slot on an entity (used on spawn/despawn by the owning system). */
export function clearStatuses(target: EntityBase): void {
  const list = target.status_effects;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    s.status_id = '';
    s.stacks = 0;
    s.remaining_s = 0;
    s.tick_rate_hz = 0;
    s.next_tick = 0;
    s.snapshot.magnitude_per_stack = 0;
    s.source_id = 0;
  }
  target.status_count = 0;
}

// --- queries (§4.1 step 3 / step 5, §03.2) ---

export function hasStatus(target: EntityBase, statusId: string): boolean {
  return findSlot(target, statusId) !== null;
}

export function statusStacks(target: EntityBase, statusId: string): number {
  if (target.status_count === 0) return 0;
  const list = target.status_effects;
  let n = 0;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    if (s.stacks > 0 && s.status_id === statusId) n += s.stacks;
  }
  return n;
}

/** Sum of `magnitude × stacks` for one status id, using the snapshotted (post-resistance) values. */
function magnitudeSum(target: EntityBase, statusId: string): number {
  if (target.status_count === 0) return 0;
  const list = target.status_effects;
  let total = 0;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    if (s.stacks > 0 && s.status_id === statusId) total += s.snapshot.magnitude_per_stack * s.stacks;
  }
  return total;
}

/** §4.1 step 3 — `status.weaken` raises damage taken. */
export function statusDamageTakenBonus(target: EntityBase): number {
  return magnitudeSum(target, STATUS_WEAKEN);
}

/** §4.4 — `status.corrode` shreds armor before the 80% mitigation clamp. */
export function statusArmorShred(target: EntityBase): number {
  return magnitudeSum(target, STATUS_CORRODE);
}

/** Movement multiplier from `status.slow`; total slow clamped at 40% (§4.9). */
export function statusSlowFactor(target: EntityBase): number {
  if (target.status_count === 0) return 1;
  let slow = magnitudeSum(target, STATUS_SLOW);
  if (slow <= 0) return 1;
  if (slow > SLOW_CAP) slow = SLOW_CAP;
  return 1 - slow;
}

export function statusStunned(target: EntityBase): boolean {
  return target.status_count > 0 && hasStatus(target, STATUS_STUN);
}

// --- step 11 ---

function stepEntityStatuses(w: World, ent: EntityBase): void {
  if (ent.status_count === 0) return;
  const list = ent.status_effects;
  let live = 0;
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    const s = list[i];
    if (s.stacks <= 0) continue;
    s.remaining_s -= STEP;
    if (s.tick_rate_hz > 0) {
      const period = periodTicks(s.tick_rate_hz);
      let guard = 0;
      while (s.next_tick <= w.tick && ++guard <= 8) {
        dealStatusDamage(w, ent, s.snapshot.magnitude_per_stack * s.stacks, s.status_id, s.source_id);
        s.next_tick += period;
        if (!ent.alive) break;
      }
    }
    if (s.remaining_s <= 1e-9 || !ent.alive) {
      s.status_id = '';
      s.stacks = 0;
      s.remaining_s = 0;
      s.tick_rate_hz = 0;
      s.next_tick = 0;
      s.snapshot.magnitude_per_stack = 0;
      s.source_id = 0;
    } else {
      live++;
    }
  }
  ent.status_count = live;
}

export function stepStatus(w: World): void {
  const p = w.player;
  if (p.alive && p.status_count > 0) stepEntityStatuses(w, p);
  const live = w.enemies.liveIndices();
  for (let i = 0; i < live.length; i++) {
    const e = w.enemies.slots[live[i]];
    if (!e.alive || e.death_anim > 0 || e.status_count === 0) continue;
    stepEntityStatuses(w, e);
  }
}
